import { useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import PersonFieldsCells, { getPersonUpdates, validPerformance } from './person-fields'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  createLuminaTeamFromProcesses,
  getLuminaProcesses,
  previewLuminaProcesses,
  LuminaProcess,
  LuminaProcessCreation,
  LuminaProcessPreview,
} from './services'

interface Props {
  onClose: () => void
  onCreated: () => Promise<void>
}

const dateLabel = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }) : 'Sin fecha'
const errorLabel = (error: unknown): string =>
  isAxiosError(error)
    ? error.response?.data?.message || 'No se pudo completar la operación. Inténtalo de nuevo.'
    : 'No se pudo completar la operación. Inténtalo de nuevo.'

const CreateTeamFromProcessesDialog = ({ onClose, onCreated }: Props) => {
  const [step, setStep] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [items, setItems] = useState<LuminaProcess[]>([])
  const [total, setTotal] = useState<number>(0)
  const [selected, setSelected] = useState<Map<number, LuminaProcess>>(new Map())
  const [loading, setLoading] = useState<boolean>(true)
  const [busy, setBusy] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [preview, setPreview] = useState<LuminaProcessPreview | null>(null)
  const [choices, setChoices] = useState<Record<number, number>>({})
  const [performanceDrafts, setPerformanceDrafts] = useState<Record<number, string>>({})
  const [cargoDrafts, setCargoDrafts] = useState<Record<number, string>>({})
  const [name, setName] = useState<string>(`Equipo de procesos - ${new Date().toLocaleDateString('es-CO')}`)
  const [description, setDescription] = useState<string>('')
  const [result, setResult] = useState<LuminaProcessCreation | null>(null)
  const [reload, setReload] = useState<number>(0)
  const requestId = useRef<number>(0)
  const invalidDates: boolean = from !== '' && to !== '' && from > to
  const unresolved: number =
    preview?.people.filter((person) => person.sources.length > 1 && !choices[person.user_id]).length || 0
  const invalidPerformance: boolean = Object.values(performanceDrafts).some((value) => !validPerformance(value))
  const missingPerformance: number = preview?.people.filter((person) => !performanceDrafts[person.user_id]?.trim()).length || 0

  useEffect(() => {
    if (step !== 0 || invalidDates) {
      setLoading(false)
      return
    }
    const id: number = ++requestId.current
    setLoading(true)
    const timer: ReturnType<typeof setTimeout> = setTimeout(async () => {
      try {
        const data = await getLuminaProcesses({
          search: search.trim() || undefined,
          from: from || undefined,
          to: to || undefined,
          page: page + 1,
          page_size: 10,
        })
        if (requestId.current === id) {
          setItems(data.items)
          setTotal(data.total)
          setError('')
        }
      } catch (failure: unknown) {
        if (requestId.current === id) {
          setItems([])
          setTotal(0)
          setError(errorLabel(failure))
        }
      } finally {
        if (requestId.current === id) setLoading(false)
      }
    }, 300)
    return () => {
      clearTimeout(timer)
      requestId.current += 1
    }
  }, [search, from, to, page, step, invalidDates, reload])

  const review = async (): Promise<void> => {
    setBusy(true)
    setError('')
    try {
      const data = await previewLuminaProcesses([...selected.keys()])
      setPreview(data)
      setChoices({})
      setPerformanceDrafts(Object.fromEntries(data.people.map((person) => [person.user_id, person.desempeno == null ? '' : String(person.desempeno)])))
      setCargoDrafts(Object.fromEntries(data.people.map((person) => [person.user_id, person.cargo || ''])))
      setStep(1)
    } catch (failure: unknown) {
      setError(errorLabel(failure))
    } finally {
      setBusy(false)
    }
  }

  const create = async (): Promise<void> => {
    if (!preview || invalidPerformance) return
    setBusy(true)
    setError('')
    try {
      const created = await createLuminaTeamFromProcesses({
        name: name.trim(),
        description: description.trim(),
        process_ids: [...selected.keys()],
        preview_hash: preview.preview_hash,
        selections: Object.entries(choices).map(([userId, processId]) => ({
          user_id: Number(userId),
          process_id: processId,
        })),
        person_updates: getPersonUpdates(preview.people, cargoDrafts, performanceDrafts),
      })
      setResult(created)
      try {
        await onCreated()
      } catch {
        setError('El equipo se creó. Actualiza la lista de equipos para verlo.')
      }
    } catch (failure: unknown) {
      setError(errorLabel(failure))
      if (isAxiosError(failure) && failure.response?.status === 409 && !/nombre/i.test(errorLabel(failure))) {
        setPreview(null)
        setChoices({})
        setStep(0)
        setReload((value) => value + 1)
      }
    } finally {
      setBusy(false)
    }
  }

  const exclusions = result?.excluded || preview?.excluded || []
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose()
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Crear equipo desde procesos</DialogTitle>
      <DialogContent sx={{ minHeight: 420 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ my: 2 }}
            action={
              step === 0 ? (
                <Button color="inherit" onClick={() => setReload((value) => value + 1)}>
                  Reintentar
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}
        {result ? (
          <>
            <Alert severity="success" sx={{ my: 2 }}>
              El equipo «{result.team.name}» se creó con {result.included.length} personas.
            </Alert>
            <Typography variant="h6">Personas incorporadas</Typography>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Persona</TableCell>
                    <TableCell>Proceso de origen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.included.map((person) => (
                    <TableRow key={person.user_id}>
                      <TableCell>{person.full_name}</TableCell>
                      <TableCell>
                        {preview?.processes.find((process) => process.id === person.process_id)?.name}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Stepper activeStep={step} sx={{ py: 3 }} alternativeLabel>
              {['Seleccionar procesos', 'Revisar personas', 'Confirmar equipo'].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {step === 0 && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Solo se muestran procesos creados con pruebas BETESA y/o 3D Skills. Se incorporarán únicamente
                  personas con resultados finalizados compatibles. Si una persona ha realizado varias pruebas del
                  mismo tipo dentro de un proceso, se usarán los datos de la última prueba finalizada y activa.
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    label="Nombre del proceso"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value)
                      setPage(0)
                    }}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 150 }}
                  />
                  <TextField
                    label="Creado desde"
                    type="date"
                    value={from}
                    onChange={(event) => {
                      setFrom(event.target.value)
                      setPage(0)
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    label="Creado hasta"
                    type="date"
                    value={to}
                    onChange={(event) => {
                      setTo(event.target.value)
                      setPage(0)
                    }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={invalidDates}
                    helperText={invalidDates ? 'La fecha final debe ser igual o posterior.' : undefined}
                    sx={{ minWidth: 180 }}
                  />
                  <Button
                    onClick={() => {
                      setSearch('')
                      setFrom('')
                      setTo('')
                      setPage(0)
                    }}
                  >
                    Limpiar
                  </Button>
                </Stack>
                <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
                  {selected.size} procesos seleccionados · La selección se conserva al buscar y cambiar de página
                  (máximo 100).
                </Typography>
                {selected.size > 0 && (
                  <Stack
                    direction="row"
                    useFlexGap
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: 2, maxHeight: 100, overflowY: 'auto' }}
                  >
                    {[...selected.values()].map((process) => (
                      <Chip
                        key={process.id}
                        label={process.name}
                        onDelete={() =>
                          setSelected((current) => {
                            const next = new Map(current)
                            next.delete(process.id)
                            return next
                          })
                        }
                      />
                    ))}
                  </Stack>
                )}
                {loading ? (
                  <Box display="flex" justifyContent="center" p={6}>
                    <CircularProgress aria-label="Cargando procesos" />
                  </Box>
                ) : (
                  !invalidDates && (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Proceso</TableCell>
                              <TableCell>Fecha de creación</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {items.map((process) => (
                              <TableRow key={process.id} hover selected={selected.has(process.id)}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selected.has(process.id)}
                                    disabled={!selected.has(process.id) && selected.size >= 100}
                                    inputProps={{ 'aria-label': `Seleccionar ${process.name}` }}
                                    onChange={(_, checked) =>
                                      setSelected((current) => {
                                        const next = new Map(current)
                                        if (checked) next.set(process.id, process)
                                        else next.delete(process.id)
                                        return next
                                      })
                                    }
                                  />
                                </TableCell>
                                <TableCell>{process.name}</TableCell>
                                <TableCell>{dateLabel(process.created_at)}</TableCell>
                              </TableRow>
                            ))}
                            {!items.length && (
                              <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                                  No hay procesos que coincidan con los filtros.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={10}
                        rowsPerPageOptions={[10]}
                        onPageChange={(_, next) => setPage(next)}
                        labelDisplayedRows={({ from: start, to: end, count }) => `${start}–${end} de ${count}`}
                        getItemAriaLabel={(type) => (type === 'next' ? 'Página siguiente' : 'Página anterior')}
                      />
                    </>
                  )
                )}
              </>
            )}
            {step === 1 && preview && (
              <>
                <Alert severity={unresolved ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {preview.people.length} personas únicas disponibles.{' '}
                  {unresolved
                    ? `Elige el proceso de origen para ${unresolved} personas repetidas.`
                    : 'Cada persona se incorporará una sola vez.'}{' '}
                  Se usarán los resultados de la última prueba finalizada y activa de cada tipo en el proceso de origen elegido.
                </Alert>
                {!preview.people.length && (
                  <Alert severity="warning">
                    Los procesos seleccionados no tienen personas con resultados finalizados compatibles.
                  </Alert>
                )}
                {preview.people.length > 0 && (
                  <Alert severity={missingPerformance ? 'warning' : 'info'} sx={{ mb: 2 }}>
                    El desempeño es opcional para crear el equipo y ver Mapas Descriptivos. Para abrir el 9Box,
                    todas las personas deben tenerlo informado. Puedes añadirlo ahora o después en Personas.
                    {missingPerformance > 0 && ` Falta el desempeño de ${missingPerformance} ${missingPerformance === 1 ? 'persona' : 'personas'}. Sin este valor, el 9Box no se podrá generar.`}
                    {' '}Cargo y desempeño se guardan al crear el equipo y se comparten con Personas y los demás equipos.
                    Escala de desempeño: de 1 a 10, con hasta dos decimales.
                  </Alert>
                )}
                <TableContainer sx={{ maxHeight: 380 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Persona</TableCell>
                        <TableCell>Documento</TableCell>
                        <TableCell>Origen de los resultados</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell sx={{ minWidth: 205 }}>Desempeño (opcional)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.people.map((person) => (
                        <TableRow key={person.user_id}>
                          <TableCell>
                            {person.full_name}
                            {person.email && <Typography variant="caption" display="block" color="text.secondary">{person.email}</Typography>}
                          </TableCell>
                          <TableCell>{person.document || 'Sin documento'}</TableCell>
                          <TableCell>
                            {person.sources.length === 1 ? (
                              <Typography variant="body2">{person.sources[0].process_name}</Typography>
                            ) : (
                              <FormControl fullWidth size="small" error={!choices[person.user_id]}>
                                <InputLabel id={`source-${person.user_id}`}>Escoge el proceso</InputLabel>
                                <Select
                                  labelId={`source-${person.user_id}`}
                                  label="Escoge el proceso"
                                  value={choices[person.user_id] || ''}
                                  onChange={(event) =>
                                    setChoices((current) => ({
                                      ...current,
                                      [person.user_id]: Number(event.target.value),
                                    }))
                                  }
                                >
                                  {person.sources.map((source) => (
                                    <MenuItem key={source.process_id} value={source.process_id}>
                                      {source.process_name} ·{' '}
                                      {source.evaluations
                                        .map(
                                          (evaluation) =>
                                            `${evaluation.category_id === 1 ? 'BETESA' : '3D Skills'} (${dateLabel(
                                              evaluation.answer_date
                                            )})`
                                        )
                                        .join(' / ')}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {person.sources
                              .filter(
                                (source) =>
                                  source.process_id ===
                                  (choices[person.user_id] ||
                                    (person.sources.length === 1 ? person.sources[0].process_id : 0))
                              )
                              .map((source) => (
                                <Typography key={source.process_id} variant="caption" color="text.secondary">
                                  {source.evaluations
                                    .map(
                                      (evaluation) =>
                                        `${evaluation.category_id === 1 ? 'BETESA' : '3D Skills'} · ${dateLabel(
                                          evaluation.answer_date
                                        )}`
                                    )
                                    .join(' / ')}
                                </Typography>
                              ))}
                          </TableCell>
                          <PersonFieldsCells name={person.full_name} cargo={cargoDrafts[person.user_id] || ''}
                            performance={performanceDrafts[person.user_id] || ''}
                            onCargoChange={(value) => setCargoDrafts((current) => ({ ...current, [person.user_id]: value }))}
                            onPerformanceChange={(value) => setPerformanceDrafts((current) => ({ ...current, [person.user_id]: value }))} />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {step === 2 && preview && (
              <Stack spacing={3} sx={{ py: 2 }}>
                <TextField
                  label="Nombre del equipo"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  inputProps={{ maxLength: 150 }}
                  fullWidth
                  helperText="Puedes cambiar el nombre sugerido para identificar tu equipo."
                />
                <TextField
                  label="Descripción (opcional)"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  multiline
                  rows={2}
                  fullWidth
                />
                <Alert severity="info">
                  Se creará un único equipo con {preview.people.length} personas de {selected.size} procesos
                  seleccionados. Se conservarán el origen y los resultados elegidos para sus analíticas.
                </Alert>
                {missingPerformance > 0 && (
                  <Alert severity="warning">
                    Se creará el equipo sin desempeño para {missingPerformance} {missingPerformance === 1 ? 'persona' : 'personas'}. Podrás ver Mapas
                    Descriptivos, pero deberás completar esos valores en Personas antes de abrir el 9Box.
                  </Alert>
                )}
                <Typography variant="body2">
                  Puedes volver al paso anterior para revisar todas las personas y sus orígenes antes de crear el
                  equipo.
                </Typography>
              </Stack>
            )}
          </>
        )}
        {(step > 0 || result) && exclusions.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#fff8ed', borderRadius: 2 }}>
            <Typography fontWeight={700}>Evaluaciones no incorporadas ({exclusions.length})</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Estas pruebas no se usarán. Una persona puede entrar al equipo si tiene otra prueba compatible finalizada.
            </Typography>
            <TableContainer sx={{ maxHeight: 200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Persona</TableCell>
                    <TableCell>Proceso</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exclusions.map((item) => (
                    <TableRow key={item.user_test_id}>
                      <TableCell>{item.full_name}</TableCell>
                      <TableCell>
                        {preview?.processes.find((process) => process.id === item.process_id)?.name}
                      </TableCell>
                      <TableCell>{item.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #edf0f3' }}>
        <Button onClick={onClose} disabled={busy}>
          {result ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!result && (
          <>
            {step > 0 && (
              <Button
                disabled={busy}
                onClick={() => {
                  setStep(step - 1)
                  setError('')
                }}
              >
                Atrás
              </Button>
            )}
            <Button
              variant="contained"
              disabled={
                busy ||
                (step === 0 && (!selected.size || invalidDates)) ||
                (step === 1 && (!preview?.people.length || unresolved > 0 || invalidPerformance)) ||
                (step === 2 && (!name.trim() || invalidPerformance))
              }
              onClick={() => {
                if (step === 0) void review()
                else if (step === 1) setStep(2)
                else void create()
              }}
              sx={{ textTransform: 'none', bgcolor: '#ad0060', '&:hover': { bgcolor: '#8f215d' } }}
            >
              {busy ? (
                <CircularProgress size={20} color="inherit" />
              ) : step === 0 ? (
                'Revisar personas'
              ) : step === 1 ? (
                'Continuar'
              ) : (
                'Crear equipo'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateTeamFromProcessesDialog
